/**
 * The filter, as a URL (ORB-759).
 *
 * A filtered list has to be shareable — pasted into Slack, bookmarked, reloaded — so the AST lives
 * in the query string rather than in component state that dies with the tab. It is also most of a
 * saved view for free: when a saved-view entity finally exists, what it stores is this string.
 *
 * The encoding is deliberately readable rather than base64. `?f=(ap.last_engaged_at~wld~n30)` says
 * "within the last 30 days" on the face of it, which is the only way to see at a glance that a
 * relative date stayed relative instead of being resolved to a timestamp at save time.
 *
 * ⚑ Every structural character is one `encodeURIComponent` leaves alone — `( ) ~ * ! ' _`. That is
 * what lets the whole string be encoded exactly once on the way out and decoded exactly once on the
 * way in (preact-iso hands `location.query` back through `URLSearchParams`, which has already
 * decoded). Escaping a structural character as a percent sequence instead would be undone by that
 * decode and reintroduce the ambiguity, so text escapes use `'` and a letter and never a `%`.
 *
 * Nothing here throws. A hand-mangled parameter yields `EMPTY_FILTER`, which the adapter reads as
 * everything — a stale bookmark lands on the full list rather than on an error boundary.
 *
 * ⚑⚑ Two different failures, and they get different dispositions (ORB-793).
 *
 * A parameter the GRAMMAR cannot read — an unclosed group, a dangling escape, a value that is not a
 * value — is mangled, and nothing in it can be trusted, so the whole thing yields `EMPTY_FILTER` as
 * before. But a clause the grammar reads perfectly well and the CATALOGUE will not accept — an
 * unpublished key, an operator the field's type does not offer — is one clause being wrong, and it
 * used to take every other clause down with it in silence. `Seniority is CXO · Company country is
 * united kingdom · Staff is at least 200` arrived with two clauses the catalogue refused and landed
 * on all 9,989 people with no filter and no complaint.
 *
 * So a rejected clause is now dropped ALONE and REPORTED. `readFilter` returns what applied and what
 * did not; the arrival strip says so in words. All-or-nothing was defensible for a URL somebody
 * mangled by hand and indefensible for a handoff we generated ourselves — and a list narrowed by two
 * of three clauses, with the third named on screen, beats an unfiltered list of everything.
 *
 * ⚑ Version 2 (ORB-815) names each clause's object in its key — `p.industry_v2`, `c.industry_v2` —
 * because one filter now holds conditions on people and on companies and the two catalogues share
 * seventeen keys. A version-1 parameter is not read: a key that never said which object it meant has
 * nothing to translate into.
 */

import {
  EMPTY_FILTER,
  type FilterAst,
  type FilterClause,
  type FilterGroup,
  type FilterNode,
  type FilterOperator,
  type FilterValue,
  type Lens,
} from './adapter'
import { fieldFor, fieldLabel, operatorsFor, type ConsoleObject, type FieldType } from './fieldCatalogue'

/** Bumped when the grammar below changes. A parameter carrying any other version is ignored. */
export const FILTER_VERSION = '2'
export const VERSION_PARAM = 'v'
export const FILTER_PARAM = 'f'
export const LENS_PARAM = 'lens'

const OPERATOR_CODES: Record<FilterOperator, string> = {
  contains: 'ct',
  not_contains: 'nct',
  equals: 'eq',
  not_equals: 'neq',
  is_any_of: 'any',
  is_none_of: 'non',
  lt: 'lt',
  lte: 'lte',
  gt: 'gt',
  gte: 'gte',
  between: 'btw',
  within_last_days: 'wld',
  is_empty: 'emp',
  is_not_empty: 'nemp',
}

const OPERATOR_BY_CODE = new Map<string, FilterOperator>(
  Object.entries(OPERATOR_CODES).map(([operator, code]) => [code, operator as FilterOperator]),
)

const OBJECT_CODES: Record<ConsoleObject, string> = { person: 'p', company: 'c' }

const OBJECT_BY_CODE = new Map<string, ConsoleObject>(
  Object.entries(OBJECT_CODES).map(([object, code]) => [code, object as ConsoleObject]),
)

/** The two operators that carry no value. Spelled out here rather than imported from `filter/`,
 *  which reads this module — `data/` does not depend on the UI above it. */
const VALUELESS = new Set<FilterOperator>(['is_empty', 'is_not_empty'])

const TEXT_ESCAPES: Record<string, string> = {
  "'": 'q',
  '~': 't',
  '*': 'a',
  '(': 'l',
  ')': 'r',
  '!': 'b',
  _: 'u',
}

const TEXT_UNESCAPES = new Map(Object.entries(TEXT_ESCAPES).map(([char, code]) => [code, char]))

function escapeText(text: string): string {
  return text.replace(/['~*()!_]/g, char => `'${TEXT_ESCAPES[char]}`)
}

function unescapeText(text: string): string | null {
  let out = ''
  for (let index = 0; index < text.length; index++) {
    if (text[index] !== "'") {
      out += text[index]
      continue
    }
    const char = TEXT_UNESCAPES.get(text[index + 1])
    // A dangling escape means the parameter was cut or edited by hand. Refuse the whole thing.
    if (char === undefined) return null
    out += char
    index++
  }
  return out
}

// ---------------------------------------------------------------------------
// Encoding
// ---------------------------------------------------------------------------

function encodeScalar(value: string | number): string {
  return typeof value === 'number' ? `n${value}` : `s${escapeText(value)}`
}

function encodeValue(value: FilterValue): string {
  if (value === null) return ''
  if (Array.isArray(value)) return `a${value.map(encodeScalar).join('_')}`
  if (typeof value === 'boolean') return value ? 'b1' : 'b0'
  return encodeScalar(value)
}

function encodeClause(clause: FilterClause): string {
  // `.` never occurs in a catalogue key, and `encodeURIComponent` leaves it alone.
  const key = `${OBJECT_CODES[clause.object]}.${clause.field}`
  const parts = [key, OPERATOR_CODES[clause.operator], encodeValue(clause.value)]
  if (clause.authoredByAgent) parts.push('g')
  // A value-less clause is `field~emp`, not `field~emp~`. Trailing empties carry no information.
  while (parts.length > 2 && parts[parts.length - 1] === '') parts.pop()
  return parts.join('~')
}

function encodeGroup(group: FilterGroup): string {
  const marker = group.combinator === 'or' ? 'o' : 'a'
  return `${group.negate ? '!' : ''}(${marker}${group.children.map(encodeNode).join('*')})`
}

function encodeNode(node: FilterNode): string {
  return node.kind === 'clause' ? encodeClause(node) : encodeGroup(node)
}

/** The AST as the raw `f` value, before URL encoding. Empty when nothing is filtered. */
export function encodeFilter(ast: FilterAst): string {
  return ast.children.length === 0 ? '' : encodeGroup(ast)
}

/**
 * The Prospects surface's whole query string, built rather than merged: the filter, the lens and,
 * when there is one, the thread.
 *
 * The subtree owns its entire URL space — that is what `routes.ts` establishes — so there is no
 * foreign parameter to preserve here, and pretending otherwise would mean carrying a query the
 * console cannot interpret through every filter change.
 *
 * `threadId` is the conversation this grid is showing (ORB-799). A handoff routes with it so the
 * thread carries on here; what it said is loaded by the bar, never carried in the URL — it can
 * name companies and people, and personal or client data does not go in a query string.
 */
export function filterQuery(ast: FilterAst, lens: Lens, threadId?: string | null): string {
  const parts = [filterForStore(ast, lens)]
  if (threadId) parts.push(`t=${encodeURIComponent(threadId)}`)
  return `?${parts.join('&')}`
}

/**
 * The list as a thread stores it (ORB-796): the filter and the lens, in the same encoding the URL
 * carries, version and all. `ConsoleFilterStorage` on the API writes this byte for byte.
 *
 * ⚑⚑ The version travels WITH the value. `encodeFilter` alone is not enough: `filterReadFromQuery`
 * refuses anything whose `v` is not the current one, and a bare encoded string put back after a
 * grammar change would be decoded by the new reader as though it were new. Storing the pair is
 * what makes a stale filter fail closed.
 */
export function filterForStore(ast: FilterAst, lens: Lens): string {
  return `${VERSION_PARAM}=${FILTER_VERSION}&${FILTER_PARAM}=${encodeURIComponent(encodeFilter(ast))}&${LENS_PARAM}=${lens}`
}

/**
 * A stored list, back as a tree and a lens. The empty filter on People for null, for a version this
 * build does not read, and for anything malformed — all of which mean "open the list unfiltered",
 * a safe and visible answer rather than a broken screen.
 */
export function filterFromStore(stored: string | null | undefined): { filter: FilterAst; lens: Lens } {
  if (!stored) return { filter: EMPTY_FILTER, lens: 'people' }
  const query: Record<string, string> = {}
  for (const part of stored.split('&')) {
    const at = part.indexOf('=')
    if (at > 0) query[part.slice(0, at)] = decodeURIComponent(part.slice(at + 1))
  }
  return { filter: filterFromQuery(query), lens: lensFromQuery(query) }
}

// ---------------------------------------------------------------------------
// Decoding
// ---------------------------------------------------------------------------

/** Distinguishes "parsed as null" from "did not parse", which `null` alone cannot. */
const INVALID = Symbol('invalid')

/**
 * The parameter is not readable as this grammar. Nothing in it can be trusted, so the whole filter
 * is refused — a bookmark somebody truncated should not apply the half that survived.
 */
const MANGLED = Symbol('mangled')

/**
 * The grammar read this clause and the catalogue will not have it — an unpublished key, an operator
 * the field's type does not offer, a value the editors would never build under that operator.
 *
 * ⚑ Its own outcome, separate from MANGLED, because that distinction is the whole of ORB-793. This
 * one is a statement about ONE clause and the clauses beside it are still good; MANGLED is a
 * statement about the string. Collapsing the two is what made a model's proposal open unfiltered.
 */
const REJECTED = Symbol('rejected')

/** One clause the URL named that its object's catalogue would not accept, and why. */
export interface DroppedClause {
  object: ConsoleObject
  /** The key exactly as the parameter wrote it. */
  field: string
  reason: string
}

/** What a filter parameter yielded: the clauses that applied, and the ones that did not. */
export interface FilterRead {
  filter: FilterAst
  dropped: DroppedClause[]
}

/** Nothing applied and nothing was refused — the shape a missing parameter reads as. */
const NOTHING_READ: FilterRead = { filter: EMPTY_FILTER, dropped: [] }

function parseScalar(text: string): string | number | typeof INVALID {
  const body = text.slice(1)
  if (text[0] === 's') return unescapeText(body) ?? INVALID
  if (text[0] !== 'n') return INVALID
  const value = Number(body)
  // Non-finite is not a filter anyone can build; through the URL it is a mangled parameter.
  return body !== '' && Number.isFinite(value) ? value : INVALID
}

function parseValue(text: string): FilterValue | typeof INVALID {
  if (text === '') return null
  if (text === 'b1') return true
  if (text === 'b0') return false
  if (text[0] !== 'a') {
    const scalar = parseScalar(text)
    return scalar === INVALID ? INVALID : scalar
  }
  const body = text.slice(1)
  if (body === '') return []
  const items: Array<string | number> = []
  for (const part of body.split('_')) {
    const scalar = parseScalar(part)
    if (scalar === INVALID) return INVALID
    items.push(scalar)
  }
  return items
}

type ClauseResult = FilterClause | typeof MANGLED | typeof REJECTED

function parseClause(text: string, dropped: DroppedClause[]): ClauseResult {
  // A literal `~` inside a value is escaped, so splitting on it cannot cut a value in half.
  const parts = text.split('~')
  if (parts.length < 2 || parts.length > 4) return MANGLED

  const [qualified, code, rawValue = '', agent = ''] = parts

  // The object is grammar, not data: a key that does not say which object it is on is a parameter
  // from another version, or cut by hand, and nothing in it can be placed.
  const dot = qualified.indexOf('.')
  const object = dot > 0 ? OBJECT_BY_CODE.get(qualified.slice(0, dot)) : undefined
  if (!object) return MANGLED
  const key = qualified.slice(dot + 1)

  const reject = (reason: string): typeof REJECTED => {
    dropped.push({ object, field: key, reason })
    return REJECTED
  }

  const field = fieldFor(object, key)
  // An unknown key is a field this object's catalogue does not publish — its own `excludedFields`
  // among them, which is how absence enforces the exclusion: a hand-written URL cannot filter on a
  // field no picker offers.
  if (!field) return reject(`'${key}' is not a field on a ${object}.`)

  const label = fieldLabel(object, key)
  const operator = OPERATOR_BY_CODE.get(code)
  if (!operator) return reject(`'${code}' is not an operator this list understands.`)
  // The URL is an input surface like any other. An operator the field's type does not offer cannot
  // be built in the bar, so it must not arrive through a link either.
  //
  // ⚑ Since ORB-793 the server refuses the same clause before it can be counted, from the same
  // shared table this reads. That is what stops a wrong ANSWER; this is what stops a wrong CHIP,
  // and they are still different failures — a URL reaches here without passing the store at all.
  if (!operatorsFor(field.type).includes(operator))
    return reject(`'${label}' does not take '${operator.replace(/_/g, ' ')}'.`)

  const value = parseValue(rawValue)
  if (value === INVALID) return MANGLED
  if (!valueFits(field.type, operator, value))
    return reject(`'${label}' was given a value it cannot use.`)
  if (agent !== '' && agent !== 'g') return MANGLED

  const clause: FilterClause = { kind: 'clause', object, field: key, operator, value }
  if (agent === 'g') clause.authoredByAgent = true
  return clause
}

/**
 * Whether a value is the shape the editors would have produced for this operator.
 *
 * The type check above stops an operator the bar would never offer. This stops a value the bar
 * would never build under an operator it does offer, which fails just as quietly and looks worse:
 * `is_none_of` with a bare string reads perfectly sensibly as a chip while `evaluateClause`'s
 * `Array.isArray` guard returns false for every row, and `between` with a scalar goes further —
 * the editor renders `[0, 0]` over an AST holding something else, so the first keystroke silently
 * rewrites the clause into something the URL never said.
 *
 * A value and an operator that takes one also have to arrive together. `contains` with nothing at
 * all matches every row; an empty string is spelled `s` and is a different thing.
 */
function valueFits(type: FieldType, operator: FilterOperator, value: FilterValue): boolean {
  if (VALUELESS.has(operator)) return value === null
  if (value === null) return false

  switch (operator) {
    case 'is_any_of':
    case 'is_none_of':
      return Array.isArray(value)
    case 'between':
      return Array.isArray(value) && value.length === 2 && value.every(item => typeof item === 'number')
    // A window of zero or half a day is not one the editor can build, and every one of them matches
    // nothing at all — every date in the store is in the past.
    case 'within_last_days':
      return typeof value === 'number' && Number.isInteger(value) && value > 0
    case 'lt':
    case 'lte':
    case 'gt':
    case 'gte':
      return typeof value === 'number'
    case 'contains':
    case 'not_contains':
      return typeof value === 'string'
    // `evaluateClause` compares these with `===`, so the value has to be the type the rows hold —
    // `'500' === 500` is false for every row in the store.
    case 'equals':
    case 'not_equals':
      if (type === 'number') return typeof value === 'number'
      if (type === 'bool') return typeof value === 'boolean'
      return typeof value === 'string'
    default:
      return false
  }
}

/** Splits a group body on the separators that sit outside any nested group. */
function splitChildren(body: string): string[] | null {
  if (body === '') return []
  const parts: string[] = []
  let depth = 0
  let start = 0
  for (let index = 0; index < body.length; index++) {
    const char = body[index]
    if (char === "'") {
      // An escape consumes the next character, so a `(` inside a value never opens a group.
      index++
      continue
    }
    if (char === '(') depth++
    else if (char === ')') {
      depth--
      if (depth < 0) return null
    } else if (char === '*' && depth === 0) {
      parts.push(body.slice(start, index))
      start = index + 1
    }
  }
  if (depth !== 0) return null
  parts.push(body.slice(start))
  return parts
}

type NodeResult = FilterNode | typeof MANGLED | typeof REJECTED

function parseNode(text: string, dropped: DroppedClause[]): NodeResult {
  const negate = text.startsWith('!')
  const rest = negate ? text.slice(1) : text

  if (!rest.startsWith('(')) {
    // Only a group carries `negate`; the AST has no negated clause.
    return negate ? MANGLED : parseClause(rest, dropped)
  }
  if (!rest.endsWith(')')) return MANGLED

  const inner = rest.slice(1, -1)
  const marker = inner[0]
  if (marker !== 'a' && marker !== 'o') return MANGLED

  const parts = splitChildren(inner.slice(1))
  if (parts === null) return MANGLED

  // ⚑⚑ A rejected child no longer takes the group with it (ORB-793). It used to: one clause the
  // catalogue would not accept nulled this group, which nulled the root, which returned
  // EMPTY_FILTER — so a three-clause handoff with one bad clause opened the whole index and said
  // nothing. A MANGLED child still does propagate, because that is a statement about the string
  // rather than about one clause.
  const children: FilterNode[] = []
  for (const part of parts) {
    const child = parseNode(part, dropped)
    if (child === MANGLED) return MANGLED
    if (child === REJECTED) continue
    children.push(child)
  }

  // A group whose every child was refused says nothing, and drawing it would be an empty chip over
  // a negation the evaluator still honours — the trap `decodeFilter` names for `!(a)` below.
  if (parts.length > 0 && children.length === 0) return REJECTED

  const group: FilterGroup = {
    kind: 'group',
    combinator: marker === 'o' ? 'or' : 'and',
    children,
  }
  if (negate) group.negate = true
  return group
}

/**
 * A raw `f` value, as what applied and what was refused (ORB-793).
 *
 * The clauses this catalogue accepted come back as the filter; the ones it would not are named in
 * `dropped`, for whoever is in a position to say so — the arrival strip, today. Silence was the
 * whole defect: the caller could not tell "Webster proposed nothing" from "everything Webster
 * proposed was thrown away".
 */
export function readFilter(encoded: string | undefined | null): FilterRead {
  if (!encoded) return NOTHING_READ
  const dropped: DroppedClause[] = []
  let node: NodeResult
  try {
    node = parseNode(encoded, dropped)
  } catch {
    // The parser is recursive and the parameter is somebody else's string. Deep enough nesting
    // overflows the stack, and this runs inside `ConsoleRoot`'s initial state — a throw there is a
    // blank page, not a wide list. "Never throws" has to be true, not nearly true.
    return NOTHING_READ
  }
  // A mangled parameter reports nothing: there is no clause to name, only a string that is not one.
  if (node === MANGLED) return NOTHING_READ
  // The top level is the AST root, which is a group by definition.
  if (node === REJECTED || node.kind !== 'group') return { filter: EMPTY_FILTER, dropped }
  // A parameter that parses to no clauses is the empty filter, whatever shape it arrived in.
  // `!(a)` would otherwise be a negated nothing: invisible in the chip row, because there is no
  // clause to draw, while still carrying a negation into the evaluator.
  if (countClauses(node) === 0) return { filter: EMPTY_FILTER, dropped }
  return { filter: node, dropped }
}

/** The AST alone, for the callers that have nowhere to report a refusal. */
export function decodeFilter(encoded: string | undefined | null): FilterAst {
  return readFilter(encoded).filter
}

function countClauses(node: FilterNode): number {
  return node.kind === 'clause'
    ? 1
    : node.children.reduce((sum, child) => sum + countClauses(child), 0)
}

/**
 * The AST from the router's parsed query. A missing or unrecognised version is treated as a
 * mangled parameter rather than assumed to be this one — the grammar is expected to change, and
 * reading a future encoding with today's parser would produce a filter the URL does not describe.
 */
export function filterReadFromQuery(query: Record<string, string | undefined>): FilterRead {
  if (query[VERSION_PARAM] !== FILTER_VERSION) return NOTHING_READ
  return readFilter(query[FILTER_PARAM])
}

/** The AST alone. Same read, for the call sites with nothing to do with a refusal. */
export function filterFromQuery(query: Record<string, string | undefined>): FilterAst {
  return filterReadFromQuery(query).filter
}

/** The lens a URL names. People when it names none, which is what a bare Prospects link opens on. */
export function lensFromQuery(query: Record<string, string | undefined>): Lens {
  return query[LENS_PARAM] === 'companies' ? 'companies' : 'people'
}
